/****************
 * Trader wallet curve, reconstructed from transfers + closed-trade PnL.
 *
 * ── PORTABILITY ────────────────────────────────────────────────────────────
 *
 * This file imports NOTHING. No React, no React Native, no app modules — only
 * `fetch`, which both the phone and the browser have. It is meant to be copied
 * verbatim into the web app so both clients draw the same curve from the same
 * arithmetic. Keep it that way: if you need a hook, a store or a theme colour,
 * put it in the caller, not here.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * The backend's wallet history cannot currently produce a usable curve:
 *
 *   - Its recorder writes a row only when the balance CHANGES, and the column
 *     it samples has stopped moving. 954 real snapshots across 225 traders in
 *     seven months — under one per trader per month.
 *   - Where snapshots are missing it reconstructs as `currentWallet - PnL`,
 *     which has no term for deposits or withdrawals. For 53% of roster traders
 *     that produces a NEGATIVE starting wallet, which is impossible.
 *   - It then DELETES the negative span, hiding the very withdrawals the chart
 *     exists to reveal.
 *
 * ── THE ARITHMETIC ─────────────────────────────────────────────────────────
 *
 * Walking backwards from a known present:
 *
 *     equity(t) = equity(now) − realisedPnL(t → now) − netTransfers(t → now)
 *
 * Every term on the right is obtainable:
 *
 *   equity(now)   Binance `lead-portfolio/detail` → marginBalance
 *   realisedPnL   our own closed trades, each with an exit_time
 *   netTransfers  Binance `lead-portfolio/transfer-history`  ← the missing term
 *
 * With the transfer term present the curve cannot go negative for any reason
 * other than bad inputs, and a deposit or withdrawal shows up as the vertical
 * step it actually was.
 *
 * ── ONE HONEST CAVEAT ──────────────────────────────────────────────────────
 *
 * `marginBalance` is equity, not free wallet: it already includes unrealised
 * PnL on open positions. We hold no historical unrealised series, so the curve
 * is exact at moments when nothing is open and carries the open float
 * otherwise. That is a property of the data, not a bug to tune away — the
 * series is labelled `equity` for exactly this reason. Do not relabel it.
 ****************/

const BINANCE = 'https://www.binance.com/bapi/futures/v1/public/future/copy-trade/lead-portfolio';

/** Binance rejects requests without a browser-shaped UA. */
const HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
};

export interface Transfer {
  /** Epoch ms. */
  t: number;
  /** Signed: positive is money IN, negative is money OUT. */
  amount: number;
  coin: string;
  kind: 'DEPOSIT' | 'WITHDRAWAL';
}

export interface ClosedTrade {
  /** Epoch ms. */
  exitAt: number;
  pnl: number;
}

/** A position over its life, for the floor: the margin it held in the market. */
export interface Held {
  /** Epoch ms. */
  openAt: number;
  /** Epoch ms; null while still open. */
  closeAt: number | null;
  margin: number;
}

export interface CurvePoint {
  x: number;
  y: number;
}

export interface WalletCurve {
  points: CurvePoint[];
  /** Deposits and withdrawals, for markers on the chart. */
  transfers: Transfer[];
  /** True when every input was present. False means the curve is indicative. */
  exact: boolean;
  /** Points raised to the floor (zero, or the margin then in the market) because
   *  money moved that is not on record (2026-10-03, 10-31). */
  heldAtZero?: number;
  /** Why it is not exact, for the caller to surface. */
  note?: string;
}

/**
 * Every transfer in and out of a lead portfolio.
 *
 * **POST, not GET.** A GET returns 400 — which reads as "bad request" and is
 * easy to mistake for "no such endpoint". It cost me a wrong conclusion; the
 * route is real and unauthenticated.
 *
 * `portfolioId` is the parameter name. `leadPortfolioId` is silently accepted
 * and returns an EMPTY LIST, which is the worst possible failure mode — a
 * trader with no transfers and a trader you asked about wrongly look identical.
 */
export async function fetchTransfers(portfolioId: string): Promise<Transfer[]> {
  const out: Transfer[] = [];
  for (let page = 1; page <= 40; page += 1) {
    const response = await fetch(`${BINANCE}/transfer-history`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ portfolioId, pageNumber: page, pageSize: 200 }),
    });
    if (!response.ok) break;
    const json = (await response.json()) as {
      data?: { total?: number; list?: { time: number; coin: string; amount: number; from: string; to: string }[] };
    };
    const list = json?.data?.list ?? [];
    for (const row of list) {
      // The portfolio side is named "Lead Trading Account". Money arriving there
      // is a deposit; money leaving it is a withdrawal.
      const isDeposit = String(row.to ?? '').toLowerCase().includes('lead');
      const amount = Math.abs(Number(row.amount) || 0);
      if (!Number.isFinite(row.time) || amount === 0) continue;
      out.push({
        t: Number(row.time),
        amount: isDeposit ? amount : -amount,
        coin: String(row.coin ?? 'USDT'),
        kind: isDeposit ? 'DEPOSIT' : 'WITHDRAWAL',
      });
    }
    if (list.length === 0 || out.length >= (json?.data?.total ?? 0)) break;
  }
  return out.sort((a, b) => a.t - b.t);
}

/**
 * Current equity from Binance, or null.
 *
 * Null, never 0. The backend's own version of this call returns 0 on failure
 * and caches the 0 for an hour, so one transient hiccup shows every wallet as
 * $0 across the product. A missing number and a zero balance are different
 * facts and must not share a representation.
 */
export async function fetchCurrentEquity(portfolioId: string): Promise<number | null> {
  try {
    const response = await fetch(`${BINANCE.replace('/public/', '/friendly/')}/detail?portfolioId=${portfolioId}`, {
      headers: HEADERS,
    });
    if (!response.ok) return null;
    const json = (await response.json()) as { success?: boolean; data?: { marginBalance?: string } };
    // `success: false` means Binance no longer serves this portfolio — the
    // trader went private, closed, or was delisted. That is information, and it
    // must not be flattened into a balance.
    if (json?.success === false) return null;
    const value = Number(json?.data?.marginBalance);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

/**
 * Build the curve.
 *
 * Walks backwards from `currentEquity`, undoing each closed trade's PnL and
 * each transfer as it passes them, then reverses the result so it reads
 * left-to-right. Points are emitted at every event AND at `now`, so the x-axis
 * always ends today rather than at the last thing that happened.
 */
export function buildWalletCurve(input: {
  currentEquity: number | null;
  closedTrades: ClosedTrade[];
  transfers: Transfer[];
  /** Left edge. Pass the trader's FIRST ORDER time, not their first exit. */
  firstOrderAt?: number;
  /** Every position, closed and open, with its margin: the floor (10-31). */
  positions?: Held[];
  now?: number;
}): WalletCurve {
  const now = input.now ?? Date.now();

  /*
   * THE MONEY HISTORY CANNOT START BEFORE THE TRADING HISTORY.
   *
   * Guillaume, 2026-09-12: "Withdrawal and deposit history should go back as far
   * as PnL history. If our oldest trade registered for a trader is like January
   * 2026, we should not display his deposit and withdrawal from 2025."
   *
   * GET /traders/{uid}/transfers returns everything the venue ever reported, and
   * that is older than what we know about a trader in two ways at once: the list
   * shows deposits from before we had ever seen them trade, and -- because every
   * transfer becomes an event below -- the reconstructed line is dragged back to
   * 2025 as well, through a span containing no PnL because we hold no trades
   * there. It is a wallet balance drawn over a period we cannot account for.
   *
   * firstOrderAt is the left edge of what we know. Transfers before it are
   * dropped rather than clipped to it: a deposit that landed outside our history
   * is not "a deposit at the start of our history", and moving it there would
   * invent a step the trader never took on that day.
   *
   * Only when we HAVE a left edge. With firstOrderAt undefined there is nothing
   * to anchor to, and a filter that silently drops everything would be worse
   * than the span it removes.
   */
  const edge = input.firstOrderAt;
  const boundedTransfers = Number.isFinite(edge)
    ? input.transfers.filter((t) => t.t >= (edge as number))
    : input.transfers;

  const transfers = [...boundedTransfers].sort((a, b) => a.t - b.t);

  if (input.currentEquity === null || !(input.currentEquity > 0)) {
    return {
      points: [],
      transfers,
      exact: false,
      note: 'No current wallet available for this trader, so there is nothing to reconstruct from.',
    };
  }

  type Event = { t: number; deltaPnl: number; deltaTransfer: number };
  const events: Event[] = [];
  for (const trade of input.closedTrades) {
    if (!Number.isFinite(trade.exitAt)) continue;
    events.push({ t: trade.exitAt, deltaPnl: trade.pnl || 0, deltaTransfer: 0 });
  }
  for (const transfer of transfers) {
    events.push({ t: transfer.t, deltaPnl: 0, deltaTransfer: transfer.amount });
  }

  /*
   * THE FLOOR IS THE MARGIN IN THE MARKET (Guillaume, 2026-10-31): "never give
   * us negative values and never goes below the 'Margin in Market' value at
   * that same X moment". A wallet cannot be smaller than the margin its open
   * positions hold. Each position's open and close is an event too (moving no
   * money), so the margin is constant between two events and the floor can be
   * checked on every stretch of the line. marginBefore(t): the margin of the
   * positions open just before t, from sorted steps and a running sum.
   */
  const steps: { t: number; d: number }[] = [];
  for (const p of input.positions ?? []) {
    if (!Number.isFinite(p.openAt) || !(p.margin > 0)) continue;
    steps.push({ t: p.openAt, d: p.margin });
    events.push({ t: p.openAt, deltaPnl: 0, deltaTransfer: 0 });
    if (p.closeAt !== null && Number.isFinite(p.closeAt)) {
      steps.push({ t: p.closeAt, d: -p.margin });
      events.push({ t: p.closeAt, deltaPnl: 0, deltaTransfer: 0 });
    }
  }
  steps.sort((a, b) => a.t - b.t);
  const stepT = steps.map((x) => x.t);
  const cum: number[] = [0];
  for (const x of steps) cum.push(cum[cum.length - 1] + x.d);
  /** Margin in the market on the stretch just before t (steps strictly before t). */
  const marginBefore = (t: number): number => {
    let lo = 0, hi = stepT.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (stepT[mid] < t) lo = mid + 1; else hi = mid;
    }
    return Math.max(0, cum[lo]);
  };

  events.sort((a, b) => a.t - b.t);

  if (events.length === 0) {
    return {
      points: [{ x: input.firstOrderAt ?? now, y: input.currentEquity }, { x: now, y: input.currentEquity }],
      transfers,
      exact: false,
      note: 'No closed trades or transfers yet — the line is the current balance held flat.',
    };
  }

  // Walk backwards. `running` is the equity immediately AFTER each event; the
  // value immediately BEFORE it is `running - delta`.
  const backwards: CurvePoint[] = [{ x: now, y: input.currentEquity }];
  let running = input.currentEquity;
  // NEVER BELOW THE FLOOR (Guillaume, 2026-10-03: "I prefer having a bias
  // historical curv than an impossible one"; 10-31: the floor is the margin in
  // the market, never less than zero). Where the balance before an event would
  // be under it, money moved that is not on record (a withdrawal we never saw;
  // MEXC publishes none), so it is held at the floor there and the walk goes
  // on from it. The curve is then marked as an estimate.
  let held = 0;
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const event = events[i];
    const after = running;
    running -= event.deltaPnl + event.deltaTransfer;
    const floor = marginBefore(event.t);
    if (running < floor) {
      running = floor;
      held += 1;
    }
    // A position opening or closing moves no money: a point only where the floor lifted the line.
    const movesMoney = event.deltaPnl !== 0 || event.deltaTransfer !== 0;
    if (movesMoney || running !== after) {
      backwards.push({ x: event.t, y: after });
      // A step: the value the instant before this event landed.
      backwards.push({ x: event.t - 1, y: running });
    }
  }

  // The line starts at the first order (10-31: "X = 0 the moment we recorded
  // the first order"), or the first event, whichever is earlier -- including a
  // first open that moved no money and so left no point of its own.
  const given = input.firstOrderAt;
  const left = given !== undefined && Number.isFinite(given) ? Math.min(given, events[0].t) : events[0].t;
  if (left < backwards[backwards.length - 1].x) {
    backwards.push({ x: left, y: running });
  }

  const points = backwards.reverse();

  // Held at zero anywhere: an input is missing (a money move not on record)
  // or wrong (our own PnL, computed from inferred order prices). Said, not
  // hidden: the curve is marked as an estimate.
  const exact = held === 0;

  return {
    points,
    transfers,
    exact,
    heldAtZero: held,
    note: exact
      ? undefined
      : 'Some money moves are not on record. Where the rebuilt balance would have gone below ' +
        'the margin then in the market (or zero) it is held there.',
  };
}

/**
 * The curve's value just before t: the wallet a trade opening at t opened
 * against (Size vs Wallet, 10-31). Null when t is outside the curve.
 */
export function walletBefore(curve: WalletCurve | null | undefined, t: number): number | null {
  const pts = curve?.points;
  if (!pts || pts.length === 0 || !Number.isFinite(t) || t < pts[0].x) return null;
  let y: number | null = null;
  for (const p of pts) {
    if (p.x >= t) break;
    y = p.y;
  }
  return y ?? pts[0].y;
}

/**
 * Transfers, cached for a day.
 *
 * A trader's transfer history is a slow-moving fact — Guillaume's requirement
 * is a daily refresh, and this is the client-side half of it. The durable half
 * is a nightly backend job writing `trader_transfers`; see the integration
 * guide. The two are compatible: point `fetchTransfers` at our own API once
 * that job exists and nothing else in this file changes.
 *
 * `store` is injected so this file stays dependency-free — pass AsyncStorage on
 * the phone, `localStorage` in the browser.
 */
export interface CurveStore {
  getItem(key: string): Promise<string | null> | string | null;
  setItem(key: string, value: string): Promise<unknown> | void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export async function fetchTransfersCached(
  portfolioId: string,
  store: CurveStore | null,
  now = Date.now(),
): Promise<Transfer[]> {
  const key = `copyfi.transfers.${portfolioId}`;
  if (store) {
    try {
      const raw = await store.getItem(key);
      if (raw) {
        const cached = JSON.parse(raw) as { at: number; rows: Transfer[] };
        if (now - cached.at < DAY_MS && Array.isArray(cached.rows)) return cached.rows;
      }
    } catch {
      // A corrupt cache entry must never break the chart — fall through and refetch.
    }
  }

  const rows = await fetchTransfers(portfolioId);
  if (store) {
    try {
      await store.setItem(key, JSON.stringify({ at: now, rows }));
    } catch {
      // Storage full or unavailable. The curve still works, just uncached.
    }
  }
  return rows;
}

/**
 * The positions a trader held, as the floor reads them (10-31), from the
 * profile's own trade rows: closed ones from entry to exit, open ones from
 * entry to now.
 *
 * The margin follows the trade's own orders where the row carries them
 * (margin_steps, [ms, margin] after each fill: "we do have the orders of that
 * trade so we should know margin at entry and margin of every increases and
 * decreases"). Without them, the row's margin_dollar, else size x entry /
 * leverage, over its whole life. An open position whose opening fill is not
 * on record starts at entry_time_before, the time it was open by (a fact). A
 * row with no time at all cannot be placed and is left out (it lowers the
 * floor, never raises it).
 */
export function heldFrom(
  closed: readonly Record<string, unknown>[] | undefined,
  open: readonly Record<string, unknown>[] | undefined,
): Held[] {
  const n = (v: unknown) => (typeof v === 'number' ? v : Number(v));
  const time = (v: unknown) => (v ? new Date(String(v)).getTime() : NaN);
  const marginOf = (r: Record<string, unknown>) => {
    const m = n(r.margin_dollar);
    if (Number.isFinite(m) && m > 0) return m;
    const size = Math.abs(n(r.max_size) || n(r.initial_size) || n(r.current_size) || 0);
    const entry = n(r.avg_entry_price) || n(r.entry_price) || 0;
    const lev = n(r.leverage);
    return size > 0 && entry > 0 && lev > 0 ? (size * entry) / lev : 0;
  };
  const stepsOf = (r: Record<string, unknown>): [number, number][] | null => {
    const raw = r.margin_steps;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const out: [number, number][] = [];
    for (const p of raw) {
      if (Array.isArray(p) && Number.isFinite(n(p[0])) && Number.isFinite(n(p[1]))) out.push([n(p[0]), n(p[1])]);
    }
    return out.length ? out.sort((a, b) => a[0] - b[0]) : null;
  };
  const out: Held[] = [];
  const add = (r: Record<string, unknown>, openAt: number, closeAt: number | null) => {
    const steps = stepsOf(r);
    if (!steps) {
      const margin = marginOf(r);
      if (margin > 0) out.push({ openAt, closeAt, margin });
      return;
    }
    // One stretch per fill: from this fill to the next, at the margin after it.
    for (let i = 0; i < steps.length; i += 1) {
      const [from, margin] = steps[i];
      const to = i + 1 < steps.length ? steps[i + 1][0] : closeAt;
      if (margin > 0 && (to === null || to > from)) out.push({ openAt: from, closeAt: to, margin });
    }
  };
  for (const r of closed ?? []) {
    const openAt = time(r.entry_time), closeAt = time(r.exit_time);
    if (Number.isFinite(openAt) && Number.isFinite(closeAt)) add(r, openAt, closeAt);
  }
  for (const r of open ?? []) {
    let openAt = time(r.entry_time);
    if (!Number.isFinite(openAt)) openAt = time(r.entry_time_before);
    if (Number.isFinite(openAt)) add(r, openAt, null);
  }
  return out;
}
